/** Mirrors of the C# repository DTO records, field for field. */

import type { ListQuery, PagedResult } from '../../administration/users/userTypes';

/** Folder or file. Mirrors the C# enum, which serializes as a number. */
export const RepositoryItemType = {
  Folder: 0,
  File: 1,
} as const;

export type RepositoryItemTypeValue =
  (typeof RepositoryItemType)[keyof typeof RepositoryItemType];

/**
 * What one caller may do to one item — the per-item half of Repository's access model.
 *
 * **FIVE INDEPENDENT FLAGS**, mirroring the C# `[Flags]` enum, which serializes as a number.
 * Test with `hasPermission`, never `===`: a value is a combination.
 *
 * The module keys are the DOOR and these are the ROOM: somebody ticked for Edit who does not
 * hold `repository.update` can open the document and not change it. Never read a flag as
 * "may do"; read it as "the item allows".
 */
export const RepositoryPermissions = {
  None: 0,
  View: 1,
  Share: 2,
  Upload: 4,
  Edit: 8,
  Delete: 16,
  All: 31,
} as const;

export type RepositoryPermissions = number;

/** Whether a flag is set. One helper, so no call site writes the bitwise test itself. */
export function hasPermission(value: RepositoryPermissions, flag: number): boolean {
  return (value & flag) === flag;
}

/** Set or clear one flag, returning the new value. */
export function withPermission(
  value: RepositoryPermissions,
  flag: number,
  on: boolean,
): RepositoryPermissions {
  const next = on ? value | flag : value & ~flag;

  // Anything ticked implies View — the same normalisation the server does on write, applied
  // here so the boxes on screen never show a state the save would silently correct.
  return next === 0 ? 0 : next | RepositoryPermissions.View;
}

/**
 * The matrix columns, in the order the dialog draws them.
 *
 * One array, so the header row and every body row are generated from the same list — two
 * hand-written runs is how a column ends up meaning a different flag in the header than in the
 * cells below it.
 */
export const SHARE_COLUMNS = [
  { flag: RepositoryPermissions.View, label: "View" },
  { flag: RepositoryPermissions.Share, label: "Share" },
  { flag: RepositoryPermissions.Upload, label: "Upload" },
  { flag: RepositoryPermissions.Edit, label: "Edit" },
  { flag: RepositoryPermissions.Delete, label: "Allow delete" },
] as const;

export interface RepositoryItemListItem {
  id: string;
  name: string;
  itemType: RepositoryItemTypeValue;
  parentId: string | null;
  parentName: string | null;

  /**
   * Set only on a folder that holds controlled documents. Its presence is what says an item
   * filed here will be issued a reference number.
   */
  folderCode: string | null;

  /**
   * The module that owns this folder, resolved server-side by walking up the branch.
   *
   * When set, the folder is **write-locked**: its contents belong to that module and are
   * filed by it through code, so the UI draws no Create and no Upload here.
   */
  ownerModuleKey: string | null;

  /** Whether this row sits inside a coded branch, and would be issued a reference number. */
  isControlled: boolean;

  /**
   * The permanent identifier a controlled document is quoted by — `L2-SAL-26-001`. Null on a
   * folder, and null on a file outside a coded branch.
   */
  referenceNumber: string | null;

  /**
   * The classification's name, resolved on every read — a LIVE view, never stored, so
   * correcting the vocabulary corrects every document naming it.
   */
  classificationName: string | null;
  classificationId: string | null;

  /**
   * Whether the classification this document carries is a CONFIDENTIAL one.
   *
   * A live view through the join, like the name beside it. It rides on the row because the
   * reader is warned BEFORE opening a confidential document, and a second call to find out
   * would be one the profile makes after it is too late.
   */
  classificationIsConfidential: boolean;

  /**
   * GENERAL ACCESS on this FOLDER — what everyone in the organization gets here without
   * being named, and `None` where it is private. Meaningless on a file, which takes its
   * folder's.
   */
  publicPermissions: RepositoryPermissions;

  /**
   * What THIS CALLER may do to this item.
   *
   * **A field whose value depends on who asked**, like `isPinned` beside it. It is the ACL
   * half ONLY — the module keys narrow it further and the client already holds those, so a row
   * reading `Editor` still draws no Edit for somebody without `repository.update`. Read it as
   * "the item allows", never as "may do".
   */
  permissions: RepositoryPermissions;

  /**
   * The originating unit's CURRENT name. A live view: the business-unit code inside the
   * reference number is the snapshot, so the two disagreeing after a recode is the system
   * working rather than a fault.
   */
  originatingOrgUnitName: string | null;
  originatingOrgUnitId: string | null;

  /**
   * Null on a folder. Set on every file this module creates — an upload stores the bytes and
   * writes the row in one call, so there is no such thing as a file waiting for one.
   */
  storedFileId: string | null;

  /**
   * The attached file's plaintext size, and the name it was uploaded under.
   *
   * Read server-side by a correlated subquery rather than a navigation, so the whole stored
   * file row never rides along with a page of the tree. The DOWNLOAD is named after the
   * reference number instead — that is what the document is known by.
   */
  fileSizeBytes: number | null;
  fileOriginalName: string | null;

  /**
   * The stored file's VERIFIED media type — what the magic bytes turned out to be on write,
   * never the extension the uploader sent.
   *
   * The Preview tab renders from this and from nothing else: it is the difference between
   * showing a file because the server confirmed what it is, and showing it because somebody
   * named it `.pdf`.
   */
  fileContentType: string | null;

  description: string | null;
  /**
   * Which version of this document the current file is — 1 on upload, meaningless on a folder.
   *
   * The reference number does NOT move with it: a revision is the same document, which is the
   * whole reason that number is permanent.
   */
  versionNumber: number;

  /**
   * When this document expires — stamped at upload from the retention window in force then.
   *
   * Null on a folder and on a file outside a coded branch: retention is a controlled-document
   * obligation. **Expired is not deleted** — the document is live, readable and downloadable,
   * and what it is, is due for review.
   */
  expiresAt: string | null;

  /**
   * Whether the CALLING reader has pinned this item.
   *
   * Personal state, so two readers looking at one row legitimately see different values. It is
   * what lets the row action say *Pin* or *Unpin* honestly, and what the profile toolbar draws
   * its chip from — before it existed the only way to answer this about one document was to
   * fetch every pin the reader holds and look for the id in it.
   */
  isPinned: boolean;

  sortOrder: number;

  /** Active children. Derived — it is also what blocks a delete. */
  childCount: number;

  isActive: boolean;
  createdAt: string;
  createdByName: string | null;
  updatedAt: string | null;
  updatedByName: string | null;
}

/**
 * One segment of the path from the root to the folder on screen.
 *
 * The breadcrumb is what carries a row's position on this module — which is why the
 * repository index is a folder listing rather than a chart. Resolved server-side: two clients
 * walking the ancestry is two chances to disagree about where the reader is standing.
 */
export interface RepositoryBreadcrumbSegment {
  id: string;
  name: string;
  folderCode: string | null;
}

/** Where the reader is, and how they got there. */
export interface RepositoryFolderContext {
  /** Null at the root. */
  folder: RepositoryItemListItem | null;

  /** Root first. Empty at the root. */
  breadcrumbs: RepositoryBreadcrumbSegment[];

  /** Whether items created here are issued a reference number. */
  isControlled: boolean;

  /** When set, the folder belongs to that module and is not written to from here. */
  ownerModuleKey: string | null;
}

/**
 * The browse response — a page of rows in one folder, plus the context it sits in.
 *
 * One call, not two: the page and the breadcrumb are one answer to "what am I looking at",
 * and splitting them lets a client render one folder's contents under another folder's name
 * while the second request is in flight.
 */
export interface RepositoryBrowseResult {
  page: PagedResult<RepositoryItemListItem>;
  context: RepositoryFolderContext;
}

/** One selectable folder, for the parent picker. Unpaged. */
export interface RepositoryFolderOption {
  id: string;
  name: string;

  /** The full path from the root — two folders may share a name in different branches. */
  path: string;

  folderCode: string | null;

  /** Set when the folder belongs to another module, which makes it an invalid create target. */
  ownerModuleKey: string | null;
}

/**
 * A new folder. **No sort order** — the server appends it.
 */
export interface CreateRepositoryFolderRequest {
  parentId: string | null;
  name: string;

  /**
   * GENERAL ACCESS for the new folder. Defaults to `None`, which is PRIVATE.
   *
   * It governs what may be FILED here as well as who may read it — a confidential document is
   * refused into a folder open to the organization, and a private one is reachable only by its
   * creator, the people it is shared with, and a holder of `repository.read-all`.
   */
  publicPermissions: RepositoryPermissions;

  /** Makes this a controlled-document folder. Refused on an edit. */
  folderCode: string | null;
  description: string | null;
}

/**
 * An edit to a folder.
 *
 * **No folder code and no owner** — both would rewrite facts other rows already depend on.
 * A reference number is built from the codes above it and is permanent.
 */
export interface UpdateRepositoryFolderRequest {
  name: string;
  description: string | null;
}

/** The repository's own query, adding the folder scope to the shared list contract. */
export interface RepositoryQuery extends ListQuery {
  folderId?: string | null;
  type?: string;

  /**
   * Drops the folder scope and lists the whole tree.
   *
   * A view scope rather than a search modifier: it changes what the list IS, which is why it
   * sits in the settings cog beside Show deleted rather than in the filter rail.
   */
  allFolders?: boolean;

  /**
   * Lists only what this reader has pinned, wherever it sits in the tree.
   *
   * A second view scope, and it IMPLIES `allFolders`: a pin is a shortcut to something the
   * reader is not standing next to, so scoping it to the folder on screen would answer the one
   * question it is not being asked. The server settles that — nothing here sends both.
   */
  pinnedOnly?: boolean;

  /**
   * Lists only documents whose retention window has run out.
   *
   * A third view scope, tree-wide like Pinned: a reader asking what is overdue for review is
   * asking about the whole repository rather than the folder they are standing in.
   *
   * **Expired is not deleted.** What it lists is live, readable and downloadable — what it is,
   * is due for review. Nothing hides or destroys a document on the date passing.
   */
  expiredOnly?: boolean;

  /**
   * Lists only what this reader CREATED — "My documents".
   *
   * A VIEW, not a visibility rule: it narrows a listing to rows the caller happens to have
   * created, and changes nothing about who may read what. The id comes from the token, so there
   * is no field for it here.
   */
  mineOnly?: boolean;

  /**
   * Only items somebody has SHARED with this caller — the Shared with me view.
   *
   * Lists the GRANTS, not everything the caller can see: a public folder is readable by
   * everyone and appears here for nobody, and what they created is `mineOnly`.
   */
  sharedOnly?: boolean;
}

/**
 * One information classification a controlled document carries.
 *
 * A **catalog**, not an enum — an Information Classification Matrix is a governance policy
 * the organization writes and revises, so the four values are rows an administrator maintains.
 * Seeded on boot with the matrix's four; an administrator may delete them, and the register
 * form withholds when none are left rather than offering a required picker with nothing in it.
 */
export interface RepositoryClassificationListItem {
  id: string;
  name: string;
  description: string | null;

  /** Whether documents under it may sit in a folder shared beyond those explicitly granted. */
  isConfidential: boolean;

  /** The stored ordering value — the matrix reads least to most restricted. */
  sortOrder: number;

  /** Live documents carrying it. Derived server-side, never stored. */
  documentCount: number;

  isActive: boolean;
  createdAt: string;
  createdByName: string | null;
  updatedAt: string | null;
  updatedByName: string | null;
}

/** Create and update take the same shape — nothing on the row is permanent. */
export interface RepositoryClassificationRequest {
  name: string;
  description: string | null;
  isConfidential: boolean;
}

/** One selectable classification, for the register form's picker. */
export interface RepositoryClassificationOption {
  id: string;
  name: string;
  description: string | null;
  isConfidential: boolean;
  sortOrder: number;
}

/**
 * What the upload form STATES: the business unit this caller's files are filed under.
 *
 * Read to DISPLAY, never to seed a picker. The code is a fact about who is uploading, and the
 * permanent reference number about to be stamped carries it — so the form says which unit it
 * names and sends nothing back.
 */
export interface RepositoryUploadDefaults {
  orgUnitId: string | null;
  orgUnitName: string | null;

  /** Never null — the resolved unit's code, or the reserved `SYS`. */
  businessUnitCode: string;

  /** True when nothing resolved and the code is the reserved fallback. */
  isSystemFallback: boolean;
}

/*
  There is no business-unit OPTION type, because there is no picker: the department is the
  uploader's own, resolved server-side with SYS as the floor. Product call, 18 Sep 2026.
*/

/**
 * One superseded file in a document's history.
 *
 * The ITEM keeps the current file; this is what it replaced. A version row is written only when
 * a file is actually archived — a row with no distinct file behind it is a version that never
 * existed, which is the one thing carried over from the reference as a warning rather than a
 * pattern.
 */
export interface RepositoryDocumentVersionListItem {
  id: string;

  /** The number the document carried while this was its file. */
  versionNumber: number;

  storedFileId: string;

  /** Why it was superseded — what the next version changed. */
  changeDescription: string | null;

  fileSizeBytes: number | null;
  fileOriginalName: string | null;

  /**
   * The archived file's VERIFIED media type — what the version modal decides renderability
   * from. Never the extension on `fileOriginalName`, which is the uploader's word for what the
   * bytes are rather than what they turned out to be.
   */
  fileContentType: string | null;

  isActive: boolean;
  createdAt: string;
  createdByName: string | null;
}

/**
 * What the repository holds, and what the volume behind it has room for.
 *
 * **Used is plaintext bytes of live files** — what a reader means by "how much is in here".
 * **Total is the VOLUME's**, not a quota: this product has none, and a configured ceiling beside
 * a real one would be a number nobody set. Zeros mean the drive could not be read, which the
 * rail renders as unknown rather than as a full disk.
 */
export interface RepositoryStorage {
  usedBytes: number;
  totalBytes: number;
  availableBytes: number;
}

/** One person's access to one item, as the share dialog lists it. */
export interface RepositoryShareListItem {
  userId: string;
  /** The account's alias, falling back to its email. Never a name out of `rsys_pii`. */
  displayName: string;
  permissions: RepositoryPermissions;
  /** The second line under the name. Null where the account has none. */
  email: string | null;
  /** True for the creator, whose boxes are ticked, disabled, and have no Remove. */
  isCreator: boolean;
}

/** What the share dialog opens onto. */
export interface RepositoryShareSummary {
  itemId: string;
  itemName: string;
  /** Whether the cascade tick is drawn, and whether general access can be set here. */
  isFolder: boolean;
  /** Read off the FOLDER — a file has none of its own. */
  publicPermissions: RepositoryPermissions;
  /** True where general access came from an ancestor rather than this row. */
  publicPermissionsAreInherited: boolean;
  people: RepositoryShareListItem[];
  /** Owner on the item AND the `repository.share` key, resolved server-side. */
  canManage: boolean;
}

/** One row of the matrix as the dialog sends it back. */
export interface RepositoryShareInput {
  userId: string;
  permissions: RepositoryPermissions;
}

/**
 * The whole people-table, saved at once.
 *
 * **It REPLACES the list** — whoever is missing is revoked in the same transaction that grants
 * the rest, which is what makes Cancel mean something.
 */
export interface SaveRepositorySharesRequest {
  people: RepositoryShareInput[];
  /** Write a grant on every descendant too. Folders only. */
  applyToChildren: boolean;
}

export interface SetRepositoryGeneralAccessRequest {
  permissions: RepositoryPermissions;
}
